/**
 * Dashboard Page (/_app/_main/)
 *
 * Command center view giving users instant situational awareness
 * of their AI delivery pipeline: stats, pipeline, attention items,
 * activity feed, recent requests, and projects.
 */

import type {
  RequestPriority as DbRequestPriority,
  RequestStatus as DbRequestStatus,
} from '@cortex/database';
import {
  Button,
  Card,
  EmptyState,
  Grid,
  GridItem,
  ProjectCard,
  type ProjectStatus,
  RequestCard,
  type RequestSummary,
  Section,
  Stack,
  Text,
} from '@cortex/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';

import {
  DashboardActivity,
  DashboardGreeting,
  DashboardPipeline,
  DashboardStats,
  type DashboardStatsData,
  NeedsAttentionList,
} from '@/components/dashboard';
import {
  NeedsAttentionSkeleton,
  PipelineBarSkeleton,
  ProjectCardSkeleton,
  RequestCardSkeleton,
  StatCardSkeletonRow,
} from '@/components/skeletons';
import {
  useDashboardActivity,
  useDashboardNeedsAttention,
  useDashboardProjects,
  useDashboardRecentRequests,
  useDashboardStats,
} from '@/hooks/use-dashboard-queries';
import {
  mapDbPriorityToUiPriority,
  mapDbStatusToUiStatus,
} from '@/lib/request-mappers';

export const Route = createFileRoute('/_app/_main/')({
  component: DashboardPage,
});

const MAX_RECENT_REQUESTS = 4;
const MAX_DASHBOARD_PROJECTS = 3;
const DEFAULT_PROJECT_STATUS: ProjectStatus = 'active';

const EMPTY_SUMMARY: RequestSummary = {
  draft: 0,
  pending: 0,
  inProgress: 0,
  review: 0,
  completed: 0,
};

function DashboardPage() {
  const navigate = useNavigate();

  // ── All queries go through the API ─────────────────────────────
  const { data: statsData, isPending: isLoadingStats } = useDashboardStats();

  const {
    data: projectsData,
    isPending: isLoadingProjects,
    error: projectsError,
  } = useDashboardProjects();

  const {
    data: requests,
    isPending: isLoadingRequests,
    error: requestsError,
  } = useDashboardRecentRequests(MAX_RECENT_REQUESTS);

  const { data: attentionRequests, isPending: isLoadingAttention } =
    useDashboardNeedsAttention();

  const {
    data: activities,
    isPending: isLoadingActivity,
    refetch: refetchActivity,
  } = useDashboardActivity();

  // ── Derived data ─────────────────────────────────────────────
  const openProjectCount =
    projectsData?.projects.filter((p) => {
      const summary = projectsData.summaries[p.id];
      if (!summary) return true;
      return (
        summary.draft > 0 ||
        summary.pending > 0 ||
        summary.inProgress > 0 ||
        summary.review > 0
      );
    }).length ?? 0;

  const stats: DashboardStatsData = {
    totalActive: statsData?.totalActive ?? 0,
    awaitingReview: statsData?.byStatus?.['review'] ?? 0,
    completedThisMonth: statsData?.completedThisMonth ?? 0,
    openProjects: openProjectCount,
  };

  const dashboardProjects =
    projectsData?.projects.slice(0, MAX_DASHBOARD_PROJECTS) ?? [];

  // ── Navigation helpers ───────────────────────────────────────
  const handleRequestClick = (requestNumber: number) => {
    navigate({ to: '/requests/$id', params: { id: String(requestNumber) } });
  };

  const handleProjectClick = (projectSlug: string) => {
    navigate({ to: '/projects/$slug', params: { slug: projectSlug } });
  };

  return (
    <Stack gap="lg" className="p-6">
      {/* Greeting + Quick Actions */}
      <DashboardGreeting />

      {/* Stats Row */}
      {isLoadingStats ? (
        <StatCardSkeletonRow />
      ) : (
        <DashboardStats data={stats} />
      )}

      {/* Pipeline */}
      {isLoadingStats ? (
        <PipelineBarSkeleton />
      ) : (
        <DashboardPipeline byStatus={statsData?.byStatus ?? {}} />
      )}

      {/* Needs Attention + Activity Feed */}
      <Grid gap="lg" className="lg:grid-cols-5">
        <GridItem className="lg:col-span-3">
          {isLoadingAttention ? (
            <NeedsAttentionSkeleton />
          ) : (
            <NeedsAttentionList
              requests={attentionRequests ?? []}
              onRequestClick={handleRequestClick}
            />
          )}
        </GridItem>
        <GridItem className="lg:col-span-2">
          <DashboardActivity
            activities={activities ?? []}
            isLoading={isLoadingActivity}
            onActivityClick={handleRequestClick}
            onRefresh={() => refetchActivity()}
          />
        </GridItem>
      </Grid>

      {/* Recent Requests */}
      <Section
        title="Recent Requests"
        titleAction={
          <Button variant="link" asChild>
            <Link
              to="/projects"
              className="text-primary flex items-center gap-1 text-sm hover:underline"
            >
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        }
      >
        {isLoadingRequests && (
          <Grid gap="md" className="sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: MAX_RECENT_REQUESTS }).map((_, index) => (
              <RequestCardSkeleton key={index} />
            ))}
          </Grid>
        )}

        {!isLoadingRequests && requestsError && (
          <Card className="p-4">
            <Text color="error" size="sm">
              Failed to load recent requests: {requestsError.message}
            </Text>
          </Card>
        )}

        {!isLoadingRequests && !requestsError && requests?.length === 0 && (
          <EmptyState
            type="no-data"
            title="No recent requests"
            description="Create a request from a project to get started."
          />
        )}

        {!isLoadingRequests &&
          !requestsError &&
          requests &&
          requests.length > 0 && (
            <Grid gap="md" className="sm:grid-cols-2 lg:grid-cols-4">
              {requests.map((request) => (
                <RequestCard
                  key={request.id}
                  title={request.title}
                  {...(request.description != null && {
                    description: request.description,
                  })}
                  status={mapDbStatusToUiStatus(
                    request.status as DbRequestStatus
                  )}
                  {...(request.priority != null && {
                    priority: mapDbPriorityToUiPriority(
                      request.priority as DbRequestPriority
                    ),
                  })}
                  requestId={`#${request.request_number}`}
                  onCardClick={() => handleRequestClick(request.request_number)}
                />
              ))}
            </Grid>
          )}
      </Section>

      {/* Projects */}
      <Section
        title="Your Projects"
        titleAction={
          <Button variant="link" asChild>
            <Link
              to="/projects"
              className="text-primary flex items-center gap-1 text-sm hover:underline"
            >
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        }
      >
        {isLoadingProjects && (
          <Grid gap="lg" className="sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: MAX_DASHBOARD_PROJECTS }).map((_, index) => (
              <ProjectCardSkeleton key={index} />
            ))}
          </Grid>
        )}

        {!isLoadingProjects && projectsError && (
          <Card className="p-4">
            <Text color="error" size="sm">
              Failed to load projects: {projectsError.message}
            </Text>
          </Card>
        )}

        {!isLoadingProjects &&
          !projectsError &&
          projectsData?.projects.length === 0 && (
            <EmptyState
              type="no-data"
              title="No projects yet"
              description="Projects will appear here once they're created."
            />
          )}

        {!isLoadingProjects &&
          !projectsError &&
          projectsData &&
          dashboardProjects.length > 0 && (
            <Grid gap="lg" className="sm:grid-cols-2 lg:grid-cols-3">
              {dashboardProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  name={project.name}
                  {...(project.description != null && {
                    description: project.description,
                  })}
                  status={DEFAULT_PROJECT_STATUS}
                  requestSummary={
                    projectsData.summaries[project.id] ?? EMPTY_SUMMARY
                  }
                  lastUpdated={new Date(project.updated_at)}
                  onCardClick={() => handleProjectClick(project.slug)}
                />
              ))}
            </Grid>
          )}
      </Section>
    </Stack>
  );
}
